import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import PageHero from "@/components/common/PageHero";
import { nakshatraBrand, umbrellaProducts } from "@/data/siteContent";
import { useState } from "react";

const NakshatraUmbrellasPage = () => {
  useEffect(() => {
    document.title = "Nakshatra Umbrellas | Keshavlal & Company";
  }, []);

  function ProductImageSlider({ gallery, title }) {

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) =>
        prev === gallery.length - 1 ? 0 : prev + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [gallery.length]);

  return (
    <img
      src={gallery[index]}
      alt={title}
      className="safe-media-card"
    />
  );
}

  return (
    <div data-testid="nakshatra-page-root">
      <PageHero
        eyebrow="In-House Product Brand"
        title="Nakshatra Umbrellas by Keshavlal & Company"
        description="Nakshatra is the in-house umbrella brand developed and manufactured by Keshavlal & Company for retail channels, distributors, and bulk procurement programs."
        testId="nakshatra-page-hero"
        primaryCta={{ label: "Request Bulk Quote", path: "/contact" }}
      />

      <section className="border-b border-slate-200 bg-white" data-testid="nakshatra-logo-section">
        <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 py-14 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="nakshatra-logo-wrapper">
            <img
              src={nakshatraBrand.logo}
              alt="Nakshatra umbrella logo"
              className="safe-media-image mx-auto max-w-md"
              data-testid="nakshatra-logo-image"
            />
          </div>

          <div className="space-y-4 self-center" data-testid="nakshatra-description-block">
            <h2 className="text-3xl font-semibold text-slate-900" data-testid="nakshatra-description-title">
              Premium In-House Brand Under Our Manufacturing Legacy
            </h2>
            <p className="text-base leading-relaxed text-slate-700 md:text-lg" data-testid="nakshatra-description-text">
              {nakshatraBrand.description}
            </p>
            <p className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-600" data-testid="nakshatra-brand-note">
              Manufactured by Keshavlal & Company
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-slate-50" data-testid="nakshatra-catalog-section">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div
            className="rounded-2xl border border-slate-200 bg-white p-6 text-center md:p-8"
            data-testid="nakshatra-catalog-card"
          >
            <h2
              className="text-2xl font-semibold text-slate-900 md:text-3xl"
              data-testid="nakshatra-catalog-title"
            >
              Explore Our In-House Umbrella Range
            </h2>
            <p
              className="mx-auto mt-3 max-w-3xl text-sm leading-relaxed text-slate-600 md:text-base"
              data-testid="nakshatra-catalog-description"
            >
              Click the link below to check out our complete Nakshatra umbrella catalog on WhatsApp.
            </p>
            <Button
              asChild
              className="mt-5 rounded-full bg-orange-500 px-7 py-5 text-sm font-semibold text-white hover:bg-orange-600"
              data-testid="nakshatra-catalog-link-button"
            >
              <a
                href={nakshatraBrand.catalogLink}
                target="_blank"
                rel="noreferrer"
                data-testid="nakshatra-catalog-link"
              >
                Click here to view our range
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-slate-50" data-testid="nakshatra-product-grid-section">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="nakshatra-product-grid">
            {umbrellaProducts.map((product) => (
              <Card key={product.slug} className="overflow-hidden rounded-2xl border-slate-200" data-testid={`nakshatra-product-card-${product.slug}`}>
                <div className="relative" data-testid={`nakshatra-product-image-wrapper-${product.slug}`}>
                  <ProductImageSlider
                    gallery={product.gallery}
                    title={`${product.title} by Nakshatra`}
                    className="safe-media-card"
                    data-testid={`nakshatra-product-image-${product.slug}`}
                  />
                  <div className="absolute left-3 top-3 flex gap-2" data-testid={`nakshatra-product-tags-${product.slug}`}>
                    <Badge className="bg-slate-900 text-white" data-testid={`nakshatra-tag-brand-${product.slug}`}>
                      Nakshatra
                    </Badge>
                    <Badge className="bg-orange-500 text-white" data-testid={`nakshatra-tag-dispatch-${product.slug}`}>
                      Dispatch Ready
                    </Badge>
                  </div>
                </div>
                <CardContent className="space-y-3 p-5">
                  <h3 className="text-xl font-semibold text-slate-900" data-testid={`nakshatra-product-title-${product.slug}`}>
                    {product.title}
                  </h3>
                  <p className="text-sm text-slate-600" data-testid={`nakshatra-product-description-${product.slug}`}>
                    {product.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-10 text-center" data-testid="nakshatra-page-cta-block">
            <Button
              asChild
              className="rounded-full bg-orange-500 px-7 py-6 text-sm font-semibold text-white hover:bg-orange-600"
              data-testid="nakshatra-page-cta-button"
            >
              <Link to="/contact" data-testid="nakshatra-page-cta-link">
                Contact for Nakshatra Bulk Orders
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NakshatraUmbrellasPage;